import React from 'react';
import { SectionCard } from './SectionCard';
import { PanelStateMessage } from './PanelStateMessage';
import { ChartViewSelector } from './chartView/ChartViewSelector';
import { useChartView } from './chartView/useChartView';
import {
  DefaultRankingBarList,
  RankingBarChart,
  RankingPieChart,
  buildRankingChartData,
} from './chartView/RankingCharts';
import { formatMoney, formatNumber } from '../../lib/formatMoney';
import { useTheme } from '../../context/ThemeContext';

const PANEL_TITLE = 'Top Venues';
const PANEL_DESCRIPTION = 'Venues ranked by revenue contribution';

function LeadingVenueSummary({ venue, currencyCode }) {
  const { primaryColor } = useTheme();
  const revenue = Number(venue.totalRevenue || 0);
  const invoices = venue.invoiceCount || 0;
  const units = venue.quantity || 0;

  return (
    <div
      className="rounded-xl px-4 py-3 text-white shadow-sm"
      style={{
        backgroundColor: primaryColor || 'var(--brand-primary, #1A428A)',
      }}
    >
      <p className="mb-1 text-[10px] uppercase tracking-[0.1em] text-white/75">Leading Venue</p>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h3 className="truncate text-[14px] font-semibold" title={venue.venueName}>
          {venue.venueName || 'Unknown venue'}
        </h3>
        <span className="text-[12px] font-bold text-white">
          {formatMoney(revenue, { currency: currencyCode })}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <p className="text-[9px] uppercase text-white/70">Invoices</p>
          <p className="text-[12px] font-semibold">{formatNumber(invoices)}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase text-white/70">Units Sold</p>
          <p className="text-[12px] font-semibold">{formatNumber(units, { decimals: 1 })}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase text-white/70">Avg Value</p>
          <p className="text-[12px] font-semibold">
            {invoices > 0 ? formatMoney(revenue / invoices, { currency: currencyCode, decimals: 0 }) : '—'}
          </p>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="py-6 text-center text-sm text-slate-400">
      No venue data found for this period
    </div>
  );
}

function MixedCurrencyEmptyState() {
  return (
    <div className="py-6 text-center text-sm text-slate-400">
      Top venues are unavailable when multiple currencies are mixed together.
      <div className="mt-1 text-xs text-slate-500">
        Apply a currency filter to see a ranked venue list.
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="grid gap-2 sm:grid-cols-[130px_1fr_auto] sm:items-center animate-pulse">
      <div className="h-3 w-24 rounded bg-slate-200" />
      <div className="h-2.5 rounded bg-slate-200" />
      <div className="h-3 w-14 rounded bg-slate-200" />
    </div>
  );
}


function VenueDetailTable({ venues, currencyCode }) {
  return (
    <div className="border-t border-slate-200 pt-3">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left text-slate-400">
            <th className="pb-1.5 font-medium">Venue</th>
            <th className="pb-1.5 font-medium text-right">Invoices</th>
            <th className="pb-1.5 font-medium text-right">Units</th>
            <th className="pb-1.5 font-medium text-right">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {venues.map((venue) => (
            <tr key={`${venue.venueId ?? 'unknown'}-${venue.venueName}-row`} className="border-t border-slate-100">
              <td className="py-1 text-slate-700">{venue.venueName || 'Unknown / Unassigned venue'}</td>
              <td className="py-1 text-right text-slate-500">{formatNumber(venue.invoiceCount || 0)}</td>
              <td className="py-1 text-right text-slate-500">{formatNumber(venue.quantity || 0)}</td>
              <td className="py-1 text-right font-semibold text-slate-700">
                {formatMoney(venue.totalRevenue, { currency: currencyCode })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TopVenuesContent({
  venues,
  chartView,
  isCompact,
  leadingVenue,
  currencyCode,
}) {
  const { chartPalette } = useTheme();
  const chartData = buildRankingChartData(venues, 'venueName', 'totalRevenue', chartPalette);
  const formatValue = (value) => formatMoney(value, { currency: currencyCode });

  if (chartView === 'bar') {
    return (
      <RankingBarChart
        data={chartData}
        currencyCode={currencyCode}
        compact={isCompact}
        leadingTitle="Leading venue"
        leadingCaption="Primary revenue driver"
        ariaLabel="Bar chart of top venues by revenue"
      />
    );
  }

  if (chartView === 'pie') {
    return (
      <RankingPieChart
        data={chartData}
        currencyCode={currencyCode}
        compact={isCompact}
        leadingTitle="Leading venue"
        leadingCaption="Primary revenue driver"
        ariaLabel="Pie chart of top venues by revenue"
      />
    );
  }

  return (
    <>
      {leadingVenue && !isCompact && (
        <LeadingVenueSummary venue={leadingVenue} currencyCode={currencyCode} />
      )}
      <DefaultRankingBarList data={chartData} formatValue={formatValue} />
      {!isCompact && <VenueDetailTable venues={venues} currencyCode={currencyCode} />}
      <div id="top-venues-announcer" className="sr-only" aria-live="polite" />
    </>
  );
}

export function TopVenuesPanel({ topVenues, currencyCode = 'DKK', loading, error, variant = 'detailed' }) {
  const isCompact = variant === 'compact';
  const venueLimit = isCompact ? 3 : 10;
  const [chartView, setChartView] = useChartView('panelChartView.topVenues', 'bar');
  const headerAction = <ChartViewSelector value={chartView} onChange={setChartView} disabled={loading} />;

  if (error) {
    return (
      <SectionCard title={PANEL_TITLE} description={PANEL_DESCRIPTION} headerAction={headerAction}>
        <PanelStateMessage
          variant="error"
          title="Unable to load top venues"
          message={error?.message || 'Try adjusting the filters or retrying the request.'}
        />
      </SectionCard>
    );
  }

  if (loading) {
    return (
      <SectionCard title={PANEL_TITLE} description={PANEL_DESCRIPTION} headerAction={headerAction}>
        <div className="space-y-2.5">
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      </SectionCard>
    );
  }

  const venues = (topVenues || []).slice(0, venueLimit);
  const leadingVenue = venues[0] || null;

  if (venues.length === 0) {
    return (
      <SectionCard title={PANEL_TITLE} description={PANEL_DESCRIPTION} headerAction={headerAction}>
        {currencyCode === 'MIXED' ? <MixedCurrencyEmptyState /> : <EmptyState />}
      </SectionCard>
    );
  }

  return (
    <SectionCard title={PANEL_TITLE} description={PANEL_DESCRIPTION} headerAction={headerAction}>
      <div className="space-y-3">
        <TopVenuesContent
          venues={venues}
          chartView={chartView}
          isCompact={isCompact}
          leadingVenue={leadingVenue}
          currencyCode={currencyCode}
        />
      </div>
    </SectionCard>
  );
}
